import { Input, Select, Spin, Table, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { formatDate } from "../../../../services/helpFunctions/formatDateAndTime";
import { useFilePreview } from "../../completed/ReportingList/Download";
import addIcon from "../../../../Assets/svg/plusIcon.svg";
import ConfirmModal from "../../../components/confirmModal/ConfirmModal";
import Done from "../../../../Assets/svg/done.svg";
import exclamationMark from "../../../../Assets/svg/exclamationMark.svg";
import previewIcon from "./../../../../Assets/svg/table-action-preview.svg";
import { httpService } from "../../../../services/httpService.service";
import SearchIcon from "./../../../../Assets/svg/searchIcon.svg";
import Preview from "../../planning/VisitBoard/TableView/modals/Preview";
import Edit from "./modals/Edit";
import FacilityTag from "../../../components/FacilityTag/FacilityTag";
import { REPORTING_DEFAULT_COLUMNS } from "../../../components/SiteVisitColumns/siteVisitColumnOrder";
import { useSiteVisitColumns } from "../../../components/SiteVisitColumns/useSiteVisitColumns";
const { Option } = Select;

const statusMappingFieldTeamReportingStatus = (status) => {
  switch (status) {
    case 1:
      return {
        text: "Data collection",
        className: "dataCollection",
        fill: "#3B81FE",
      };
    case 2:
      return {
        text: "Data check",
        className: "dataCheck",
        fill: "#045B46",
      };
    case 3:
      return {
        text: "Field Reporting writing",
        className: "fieldReportWriting",
        fill: "var(--Range-assessment-Excellent, #3D9B4C)",
      };
    case 4:
      return {
        text: "Translation",
        className: "translation",
        fill: "#918d27",
      };
    case 5:
      return {
        text: "Field Reporting Completed",
        className: "fieldReportCompleted",
        fill: "#9670b8",
      };
    case 6:
      return {
        text: "Field report approved",
        className: "fieldReportApproved",
        fill: "var(--Graph-Green-1, #06768E)",
      };
    case 7:
      return {
        text: "Field report submitted to client",
        className: "fieldReportSubmitted",
        fill: "var(--Graph-Green-3, #6FC5D5)",
      };
    default:
      return {
        text: "Unknown",
        // className: "noStatus",
        fill: "white",
      };
  }
};

const SiteVisitReportingList = ({ loading, setLoading, setMessage }) => {
  const { previewFiles } = useFilePreview();
  const [selectedSiteVisit, setSelectedSiteVisit] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const [siteVisitCards, setSiteVisitCards] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageNumber: 1,
    total: 0,
  });
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 576);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 576);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageNumber: 1,
    }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);
  const fetchSiteVisits = () => {
    const projectId = localStorage.getItem("currentProjectID");

    const params = {
      ProjectId: projectId,
      includeOnlyWithTracking: true,
      Search: debouncedQuery,
      SiteVisitStatus: 5,
      pageSize: pagination.pageSize,
      pageNumber: pagination.pageNumber,
    };

    const filteredParams = Object.keys(params).reduce((acc, key) => {
      if (params[key] !== undefined && params[key] !== null) {
        acc[key] = params[key];
      }
      return acc;
    }, {});

    const queryString = new URLSearchParams(filteredParams).toString();

    const apiUrl = `/api/Site/getAllSiteVisits?${queryString}&ApprovalStatus=0&ApprovalStatus=3`;
    setLoading(true);
    httpService
      .get(
        apiUrl,
        (res) => {
          setSiteVisitCards(res.data);
          setPagination({
            pageSize: res.metaData.pageSize,
            pageNumber: res.metaData.pageNumber,
            total: res.metaData.totalItemCount,
          });
        },
        (error) => {
          // console.log(error);
        }
      )
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSiteVisits();
  }, [pagination.pageNumber, pagination.pageSize, debouncedQuery]);

  const columns = [
    {
      title: <div className="tableHeader">Approval Status</div>,
      dataIndex: "rejected",
      key: "rejected",
      sorter: (a, b) =>
        a.rejected.toString().localeCompare(b.rejected.toString()),
      render: (rejected, record) => {
        const tooltipContent = record?.rejectionReason
          ? `Reason: ${record.rejectionReason}`
          : "No reason provided";

        return (
          <Tooltip title={rejected ? tooltipContent : ""}>
            <div className={rejected ? "noStatus" : "dataCollection"}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="8px"
                height="8px"
                viewBox="0 0 9 9"
              >
                <circle
                  cx="4.5"
                  cy="4.27344"
                  r="4"
                  fill={!rejected ? "#3B81FE" : "#fff"}
                />
              </svg>
              <span style={rejected ? { color: "#fff" } : {}}>
                {rejected ? "Rejected" : "Not Submitted"}
              </span>
            </div>
          </Tooltip>
        );
      },
    },

    {
      title: <span className="styledTitle">Site Visit Identifier</span>,
      sorter: (a, b) =>
        a.siteVisitIdentifier.localeCompare(b.siteVisitIdentifier),
      dataIndex: "siteVisitIdentifier",
      key: "siteVisitIdentifier",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Site Visit Name</span>,
      sorter: (a, b) => a.name.localeCompare(b.name),
      dataIndex: "name",
      key: "name",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Facility</span>,
      dataIndex: "facility",
      key: "facility",
      ellipsis: true,
      width: 250,
      sorter: (a, b) =>
        (a.facility?.name || "").localeCompare(b.facility?.name || ""),
      render: (facility) => <FacilityTag facility={facility} />,
    },
    {
      title: <span className="styledTitle">Field Report Status</span>,
      dataIndex: "fieldTeamReportingStatus",
      key: "fieldTeamReportingStatus",
      width: 250,
      sorter: (a, b) => {
        const statusA = a.fieldTeamReportingStatus || "";
        const statusB = b.fieldTeamReportingStatus || "";
        return String(statusA).localeCompare(String(statusB));
      },
      render: (value, record) => {
        const { text, className, fill } = statusMappingFieldTeamReportingStatus(
          record?.fieldTeamReportingStatus
        );
        return (
          <div className={className}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="8px"
              height="8px"
              viewBox="0 0 9 9"
            >
              <circle cx="4.5" cy="4.27344" r="4" fill={fill} />
            </svg>
            <span className="styledDataIndex3">{text}</span>
          </div>
        );
      },
    },
    {
      title: <span className="styledTitle">Actual Dates of Visit</span>,
      sorter: (a, b) => {
        const aDate = a.actualDateOfVisit?.[0] || "";
        const bDate = b.actualDateOfVisit?.[0] || "";
        return aDate.localeCompare(bDate);
      },
      dataIndex: "actualDateOfVisit",
      key: "actualDateOfVisit",
      render: (data) =>
        data?.length ? (
          data?.map((date) => (
            <span
              className="styledDataIndex3"
              style={{ display: "block" }}
              key={date.id}
            >
              {date}
            </span>
          ))
        ) : (
          <span className="styledDataIndex3">
            No actual date of visit available
          </span>
        ),
    },
    {
      title: <span className="styledTitle">Location</span>,
      sorter: (a, b) =>
        a.locationDetails?.country.localeCompare(b.locationDetails?.country),
      key: "location",
      dataIndex: "location",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Governate/State</span>,
      dataIndex: "state",
      key: "state",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">IP</span>,
      sorter: (a, b) => a.ip.localeCompare(b.ip),
      dataIndex: "ip",
      key: "ip",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Cluster</span>,
      sorter: (a, b) => a.cluster.localeCompare(b.cluster),
      dataIndex: "cluster",
      key: "cluster",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    {
      title: <span className="styledTitle">Virtual</span>,
      sorter: (a, b) => a.virtual.localeCompare(b.virtual),
      dataIndex: "virtual",
      key: "virtual",
      render: (text) => <span className="styledDataIndex3">{text}</span>,
    },
    // The two dates that close a field report out. Already fetched for the edit
    // modal, so the list can show them without another request.
    {
      title: <span className="styledTitle">Date of Report</span>,
      dataIndex: "dateOfReport",
      key: "dateOfReport",
      sorter: (a, b) =>
        (a.dateOfReport || "").localeCompare(b.dateOfReport || ""),
      render: (text) => (
        <span className="styledDataIndex3">
          {text ? formatDate(text) : "—"}
        </span>
      ),
    },
    {
      title: <span className="styledTitle">Date of Debrief</span>,
      dataIndex: "dateOfDebrief",
      key: "dateOfDebrief",
      sorter: (a, b) =>
        (a.dateOfDebrief || "").localeCompare(b.dateOfDebrief || ""),
      render: (text) => (
        <span className="styledDataIndex3">
          {text ? formatDate(text) : "—"}
        </span>
      ),
    },
    {
      title: <span className="styledTitle">Resources</span>,
      sorter: (a, b) =>
        a.resources?.join("").localeCompare(b.resources?.join("")),
      dataIndex: "resources",
      key: "resources",
      render: (resources) => {
        return (
          <div className="resourcesColumn">
            {resources?.map((file, index) => (
              <span
                onClick={(e) => previewFiles(e, file.fileKey, file.id)}
                key={index}
              >
                {file?.fileName}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: <span className="styledTitle"></span>,
      key: "action",
      fixed: isLargeScreen && siteVisitCards.length > 0 ? "right" : undefined,

      render: (text, record) => (
        <div className="actionDiv">
          {/* {tracked && ( */}
          <img
            src={previewIcon}
            onClick={() => handleTogglePreviewModal(record)}
            style={{ cursor: "pointer" }}
          ></img>
          {/* )} */}
          {/* {isAuthorized([
            RolesEnum.SystemAdmin,
            RolesEnum.ProjectManager,
            RolesEnum.FieldCoordinator,
            RolesEnum.FieldLogistics,
            RolesEnum.FieldTeamLeader,
          ]) && ( */}
          <Tooltip title="Add report">
            <img
              src={addIcon}
              onClick={() => handleToggleEditModal(record)}
              style={{ cursor: "pointer" }}
            />
          </Tooltip>
          <Tooltip title="Submit to approve">
            <svg
              onClick={() => {
                ConfirmModal(
                  () => handleChangeConfirm(record?.key), // Pass the function as a callback
                  "Warning",
                  "Confirming this will send the site visit to submit approval! This action can not be undone!", // Information
                  "Do you want to continue?", // Question
                  "Yes" // Confirm button text
                );
                setLoading(false);
              }}
              style={{ cursor: "pointer" }}
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="#AA1A5F"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M6.85603 9.14565L0.570312 6.2885L15.4275 0.574219L9.71317 15.4314L6.85603 9.14565ZM6.85603 9.14565L10.2846 5.71708" />
            </svg>
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleTogglePreviewModal = (record) => {
    const siteVisitCardId = record?.key;
    setSelectedSiteVisit(siteVisitCardId);
    setShowPreviewModal(!showPreviewModal);
  };

  const handleToggleEditModal = (record) => {
    const siteVisitCardId = record?.key;
    setSelectedSiteVisit(siteVisitCardId);
    setShowEditModal(true);
  };

  const handleChangeConfirm = (SiteVisitId) => {
    if (SiteVisitId) {
      setLoading(true);
      httpService.put(
        `/api/Site/changeSiteVisitApprovalStatus?SiteVisitId=${SiteVisitId}&ApprovalStatus=1`,
        "",
        (res) => {
          const message =
            res?.message || "You have successfully updated site visit files!";
          setMessage({
            trigger: true,
            type: "success",
            icon: Done,
            title: `Site visit updated successfully!`,
            text: message,
          });

          setTimeout(() => {
            setMessage({
              trigger: false,
            });
          }, 3000);
          setLoading(false);
          fetchSiteVisits();
        },
        (error) => {
          const errorMessage =
            error?.errors?.[0]?.message ||
            error?.message ||
            "Failed to update this site visit. Please try again later!";

          setMessage({
            trigger: true,
            type: "danger",
            icon: exclamationMark,
            title: "Update Failed",
            text: errorMessage,
          });

          setTimeout(() => {
            setMessage({
              trigger: false,
            });
          }, 3000);
          setLoading(false);
        }
      );
    }
  };

  const handleEdit = () => {
    fetchSiteVisits();

    setShowEditModal(false);
  };

  const formatData = (siteVisitCard) => {
    return {
      key: siteVisitCard?.id,
      siteVisitIdentifier: siteVisitCard?.identifier,
      name: siteVisitCard?.name || "N/A",
      actualDateOfVisit: siteVisitCard?.tracking?.siteTrackingVisitDates.map(
        (date) => formatDate(date?.visitDate)
      ),
      ip: siteVisitCard?.ipPlan?.implementingPartner?.name,
      cluster: siteVisitCard?.cluster?.name,
      virtual: siteVisitCard?.isVirtual ? "Yes" : "No",
      resources: siteVisitCard?.siteVisitFiles.filter((item) =>
        [1, 3, 4, 5].includes(item.fileCategory)
      ),
      facility: siteVisitCard?.facility,
      state:
        siteVisitCard?.locationDetails?.state || "This country has no states.",
      location: siteVisitCard?.locationDetails?.country || "N/A",
      reportingYear: siteVisitCard?.tracking?.reportingYear,
      reportingMonth: siteVisitCard?.tracking?.reportingMonth,
      trainingDate: siteVisitCard?.tracking?.trainingDate,
      trainingScoreInfo: siteVisitCard?.tracking?.trainingScoreInfo,
      dateOfReport: siteVisitCard?.tracking?.dateOfReport,
      dateOfDebrief: siteVisitCard?.tracking?.dateOfDebrief,
      rejectionReason: siteVisitCard?.rejectionReason || null,
      percentageOfDataCollectionCompleted:
        siteVisitCard?.tracking?.percentageOfDataCollectionCompleted,
      fieldTeamReportingStatus:
        siteVisitCard?.tracking?.fieldTeamReportingStatus,
      trainingIsCompleted: siteVisitCard?.tracking?.trainingIsCompleted,
      rejected: siteVisitCard?.approvalStatus === 3 ? true : false,
    };
  };

  const formattedData = siteVisitCards?.map(formatData);

  const {
    columns: visibleColumns,
    columnsBar,
    components: tableComponents,
  } = useSiteVisitColumns({
    tableId: "siteVisitReportingList",
    columns,
    defaultKeys: REPORTING_DEFAULT_COLUMNS,
  });

  return (
    <>
      <div className="flagsPage mb-4">
        <span className="tableViewHeaderText">Site Visit Reporting List</span>
      </div>
      <div className="filtersWrapper">
        <div className="containerFilters">
          <Input
            onChange={handleSearch}
            placeholder="Search... "
            suffix={<img src={SearchIcon} alt="Search" />}
          />
        </div>
      </div>
      <div
        className="w-100 my-3 d-flex justify-content-start gap-2"
        style={{
          color: "var(--2, #AA1A5F)",
          fontFamily: "Poppins",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: "600",
          lineHeight: "24px",
        }}
      >
        {loading ? (
          <Spin className="me-2" size="small" spinning={loading} />
        ) : (
          `Total: ${pagination.total} visits`
        )}
        {columnsBar && <span className="ms-auto">{columnsBar}</span>}
      </div>
      <Table
        components={tableComponents}
        loading={loading}
        rowClassName={(record) =>
          record?.rejectionReason ? "rejectedRow" : ""
        }
        dataSource={formattedData}
        columns={formattedData?.length > 0 ? visibleColumns : []}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            setPagination({
              ...pagination,
              pageNumber: page,
              pageSize: pageSize,
            });
          },
        }}
        className="tableTPM"
        scroll={{ x: "max-content" }}
      />
      <Preview
        onClose={handleTogglePreviewModal}
        visible={showPreviewModal}
        siteVisitCardId={selectedSiteVisit}
      />
      <Edit
        visible={showEditModal}
        onCancel={() => setShowEditModal(false)}
        onOk={handleEdit}
        siteVisitCardId={selectedSiteVisit}
        setMessage={setMessage}
        fetchSiteVisits={fetchSiteVisits}
      />
    </>
  );
};

export default SiteVisitReportingList;
